/**
 * New Appointment Page
 *
 * Professional 5-step guided flow:
 *   1. Modalidad  ->  2. Profesional  ->  3. Fecha y Horario  ->  4. Paciente  ->  5. Confirmar
 *
 * Each step is a Card that becomes enabled only when the previous step is complete.
 * Changing an earlier step resets all subsequent steps.
 */

import { useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Users as UsersIcon,
  Loader2,
  Check,
  Search,
  Layers,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import {
  useCreateAppointment,
  useAvailableSlots,
  useAvailableDays,
  useSchedules,
} from '@/application/appointments'
import { useQuery } from '@tanstack/react-query'
import { useRadiologists } from '@/application/users/useUsers'
import { useActiveModalities } from '@/application/modalities'
import { subscriptionService } from '@/application/modality-subscriptions'
import { usePatients } from '@/application/patients'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { MultiSelect } from '@/presentation/components/ui/MultiSelect'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { APP_ROUTES } from '@/config/constants'
import type { TimeSlot } from '@/domain/appointment/types'

// =============================================================================
// Form State
// =============================================================================

interface FormState {
  modalityIds: string[]
  modalityLabels: string[]
  providerId: string
  providerLabel: string
  selectedDate: string
  scheduledTime: string
  endTime: string
  patientId: string
  patientLabel: string
  patientDocument: string
  notes: string
}

const INITIAL_FORM_STATE: FormState = {
  modalityIds: [],
  modalityLabels: [],
  providerId: '',
  providerLabel: '',
  selectedDate: '',
  scheduledTime: '',
  endTime: '',
  patientId: '',
  patientLabel: '',
  patientDocument: '',
  notes: '',
}

// =============================================================================
// Main Component
// =============================================================================

export function NewAppointmentPage() {
  const navigate = useNavigate()
  const createAppointmentMutation = useCreateAppointment()

  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [patientSearch, setPatientSearch] = useState('')

  // ---------------------------------------------------------------------------
  // Data hooks
  // ---------------------------------------------------------------------------

  const { data: modalitiesData } = useActiveModalities()
  const { data: radiologistsData } = useRadiologists()

  // Providers subscribed to ALL selected modalities
  const { data: subscribedProviderIdList } = useQuery({
    queryKey: ['appointment-modality-subscribers', formState.modalityIds],
    queryFn: async () => {
      const results = await Promise.all(
        formState.modalityIds.map((id: string) => subscriptionService.getSubscribers(id))
      )
      const subscriberSets = results.map(
        (r: { subscribers: { id: string }[] }) => new Set(r.subscribers.map((s) => s.id))
      )
      if (subscriberSets.length === 0) return [] as string[]
      const intersection = subscriberSets.reduce((acc: Set<string>, set: Set<string>) =>
        new Set([...acc].filter((id) => set.has(id)))
      )
      return [...intersection]
    },
    enabled: formState.modalityIds.length > 0,
  })

  // Schedules for selected modalities (to filter providers)
  const { data: schedulesForModality } = useSchedules(
    formState.modalityIds.length > 0
      ? { isActive: true, limit: 200 }
      : undefined
  )

  // Available days for the displayed month (use first modality for availability check)
  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`
  const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`

  const primaryModalityId = formState.modalityIds[0] || undefined

  const { data: availableDays, isLoading: daysLoading } = useAvailableDays(
    formState.providerId || undefined,
    primaryModalityId,
    formState.providerId ? monthStart : undefined,
    formState.providerId ? monthEnd : undefined
  )

  // Available slots for selected date (use first modality)
  const { data: slotsData, isLoading: slotsLoading } = useAvailableSlots(
    formState.providerId || undefined,
    primaryModalityId,
    formState.selectedDate || undefined
  )

  // Patient search
  const { data: patientsData, isLoading: patientsLoading } = usePatients(
    patientSearch.length >= 2 ? { name: patientSearch, limit: 10 } : undefined
  )

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const modalityOptions = useMemo(() => {
    if (!modalitiesData?.modalities) return []
    return modalitiesData.modalities.map((m) => ({
      value: m.id,
      label: `${m.name} (${m.code})`,
    }))
  }, [modalitiesData])

  const providerIdsWithSchedules = useMemo(() => {
    if (!schedulesForModality?.schedules || formState.modalityIds.length === 0) return null
    const selectedSet = new Set(formState.modalityIds)
    // Providers that have schedules for any of the selected modalities
    return new Set(
      schedulesForModality.schedules
        .filter((s) => selectedSet.has(s.modalityId))
        .map((s) => s.providerId)
    )
  }, [schedulesForModality, formState.modalityIds])

  const filteredProviders = useMemo(() => {
    if (!radiologistsData) return []
    let providers = radiologistsData
    // Filter by schedule availability
    if (providerIdsWithSchedules && providerIdsWithSchedules.size > 0) {
      providers = providers.filter((r) => providerIdsWithSchedules.has(r.id))
    }
    // Filter by modality subscriptions
    if (subscribedProviderIdList) {
      if (subscribedProviderIdList.length > 0) {
        const subscribedSet = new Set(subscribedProviderIdList)
        providers = providers.filter((r) => subscribedSet.has(r.id))
      } else if (formState.modalityIds.length > 0) {
        // Subscriptions loaded but no providers match all modalities
        return []
      }
    }
    return providers
  }, [radiologistsData, providerIdsWithSchedules, subscribedProviderIdList, formState.modalityIds])

  const providerOptions = useMemo(() => {
    return filteredProviders.map((r) => ({
      value: r.id,
      label: r.fullName,
    }))
  }, [filteredProviders])

  const availableDateSet = useMemo(() => {
    if (!availableDays) return new Set<string>()
    return new Set(
      availableDays
        .filter((d) => d.availableSlots > 0 && !d.isBlocked)
        .map((d) => d.date)
    )
  }, [availableDays])

  const slots: TimeSlot[] = slotsData ?? []

  // ---------------------------------------------------------------------------
  // Step enablement
  // ---------------------------------------------------------------------------

  const step2Enabled = formState.modalityIds.length > 0
  const step3Enabled = !!formState.providerId
  const step4Enabled = !!formState.scheduledTime
  const step5Enabled = !!formState.patientId

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleModalityChange(ids: string[]) {
    const labels = ids
      .map((id) => modalitiesData?.modalities.find((m) => m.id === id))
      .filter(Boolean)
      .map((m) => `${m!.name} (${m!.code})`)
    setFormState((prev) => ({
      ...prev,
      modalityIds: ids,
      modalityLabels: labels,
      providerId: '',
      providerLabel: '',
      selectedDate: '',
      scheduledTime: '',
      endTime: '',
      patientId: '',
      patientLabel: '',
      patientDocument: '',
    }))
    setPatientSearch('')
  }

  function handleProviderChange(providerId: string) {
    const provider = filteredProviders.find((r) => r.id === providerId)
    setFormState((prev) => ({
      ...prev,
      providerId,
      providerLabel: provider?.fullName ?? '',
      selectedDate: '',
      scheduledTime: '',
      endTime: '',
      patientId: '',
      patientLabel: '',
      patientDocument: '',
    }))
    setPatientSearch('')
  }

  function handleDateSelect(dateStr: string) {
    setFormState((prev) => ({
      ...prev,
      selectedDate: dateStr,
      scheduledTime: '',
      endTime: '',
    }))
  }

  function handleSlotSelect(slot: TimeSlot) {
    if (!slot.available) return
    setFormState((prev) => ({
      ...prev,
      scheduledTime: slot.time,
      endTime: slot.endTime,
    }))
  }

  function handlePatientSelect(patient: {
    id: string
    firstName: string
    secondName?: string | null
    lastName: string
    secondLastName?: string | null
    document: { type: string; number: string }
  }) {
    const fullName = [patient.firstName, patient.secondName, patient.lastName, patient.secondLastName]
      .filter(Boolean)
      .join(' ')
    setFormState((prev) => ({
      ...prev,
      patientId: patient.id,
      patientLabel: fullName,
      patientDocument: `${patient.document.type} ${patient.document.number}`,
    }))
  }

  async function handleSubmit() {
    try {
      for (const modalityId of formState.modalityIds) {
        await createAppointmentMutation.mutateAsync({
          patientId: formState.patientId,
          providerId: formState.providerId,
          modalityId,
          scheduledDate: formState.selectedDate,
          scheduledTime: formState.scheduledTime,
          endTime: formState.endTime,
          bookedVia: 'STAFF',
          notes: formState.notes || undefined,
        })
      }
      toast.success(
        formState.modalityIds.length > 1
          ? `${formState.modalityIds.length} citas agendadas correctamente`
          : 'Cita agendada correctamente'
      )
      navigate(APP_ROUTES.APPOINTMENTS)
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al agendar la cita'))
    }
  }

  // ---------------------------------------------------------------------------
  // Calendar helpers
  // ---------------------------------------------------------------------------

  function navigateMonth(delta: number) {
    setCurrentMonth((prev) => {
      const next = new Date(prev)
      next.setMonth(next.getMonth() + delta)
      return next
    })
  }

  const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ]

  const DAY_HEADERS = ['Lun', 'Mar', 'Mi\u00e9', 'Jue', 'Vie', 'S\u00e1b', 'Dom']

  function buildCalendarDays() {
    const firstDay = new Date(year, month, 1)
    // Monday-based offset: Mon=0, Tue=1, ... Sun=6
    const offset = (firstDay.getDay() + 6) % 7
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const cells: Array<{
      day: number | null
      dateStr: string
      isPast: boolean
      isAvailable: boolean
      isSelected: boolean
      isToday: boolean
    }> = []

    // Leading empty cells
    for (let i = 0; i < offset; i++) {
      cells.push({ day: null, dateStr: '', isPast: false, isAvailable: false, isSelected: false, isToday: false })
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month, d)
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const isPast = dateObj < today
      const isAvailable = availableDateSet.has(dateStr)
      const isSelected = formState.selectedDate === dateStr
      const isToday = dateObj.getTime() === today.getTime()

      cells.push({ day: d, dateStr, isPast, isAvailable, isSelected, isToday })
    }

    return cells
  }

  function formatDateSpanish(dateStr: string): string {
    const [y, m, d] = dateStr.split('-').map(Number)
    const dateObj = new Date(y, m - 1, d)
    return dateObj.toLocaleDateString('es-CO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const calendarDays = step3Enabled ? buildCalendarDays() : []

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={APP_ROUTES.APPOINTMENTS}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Nueva Cita</h1>
          <p className="text-sm text-[var(--foreground-muted)]">
            Complete cada paso para agendar una cita
          </p>
        </div>
      </div>

      {/* ================================================================= */}
      {/* Step 1: Modalidades                                               */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            1. Modalidades
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MultiSelect
            label="Seleccione una o mas modalidades"
            placeholder="-- Seleccionar --"
            options={modalityOptions}
            value={formState.modalityIds}
            onChange={handleModalityChange}
          />
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Step 2: Profesional                                               */}
      {/* ================================================================= */}
      <Card className={cn('transition-opacity', !step2Enabled && 'opacity-50 pointer-events-none')}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            2. Profesional
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            label="Seleccione un profesional"
            placeholder="-- Seleccionar --"
            options={providerOptions}
            value={formState.providerId}
            onChange={(e) => handleProviderChange(e.target.value)}
          />
          {step2Enabled && providerOptions.length === 0 && (
            <p className="mt-2 text-sm text-[var(--foreground-muted)]">
              No hay profesionales suscritos con horarios activos para las modalidades seleccionadas.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Step 3: Fecha y Horario                                           */}
      {/* ================================================================= */}
      <Card className={cn('transition-opacity', !step3Enabled && 'opacity-50 pointer-events-none')}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            3. Fecha y Horario
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mini calendar */}
          <div>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold text-[var(--foreground)]">
                {MONTH_NAMES[month]} {year}
              </span>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAY_HEADERS.map((d) => (
                <div
                  key={d}
                  className="text-center text-xs font-medium text-[var(--foreground-muted)] py-1"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((cell, idx) => {
                if (cell.day === null) {
                  return <div key={`empty-${idx}`} />
                }

                const disabled = cell.isPast
                const isAvailable = cell.isAvailable && !cell.isPast

                return (
                  <button
                    key={cell.dateStr}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleDateSelect(cell.dateStr)}
                    className={cn(
                      'relative h-9 w-full rounded-md text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                      // Base
                      'text-[var(--foreground)]',
                      // Disabled / past
                      disabled && 'text-[var(--foreground-muted)] opacity-40 cursor-not-allowed',
                      // Available (not selected)
                      isAvailable && !cell.isSelected && 'ring-1 ring-[var(--primary)] bg-[var(--primary)]/5 hover:bg-[var(--primary)]/15 cursor-pointer',
                      // Not available, not past, not selected
                      !isAvailable && !disabled && !cell.isSelected && 'hover:bg-[var(--accent)] cursor-pointer',
                      // Selected
                      cell.isSelected && 'bg-[var(--primary)] text-[var(--primary-foreground)]',
                      // Today indicator
                      cell.isToday && !cell.isSelected && 'font-bold',
                    )}
                  >
                    {cell.day}
                    {cell.isToday && !cell.isSelected && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[var(--primary)]" />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Loading indicator */}
            {daysLoading && step3Enabled && (
              <div className="flex items-center justify-center gap-2 mt-3 text-sm text-[var(--foreground-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando disponibilidad...
              </div>
            )}
          </div>

          {/* Time slot grid */}
          {formState.selectedDate && (
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                <Clock className="inline h-4 w-4 mr-1 -mt-0.5" />
                Horarios disponibles para {formatDateSpanish(formState.selectedDate)}
              </label>

              {slotsLoading ? (
                <div className="flex items-center gap-2 text-sm text-[var(--foreground-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando horarios...
                </div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-[var(--foreground-muted)]">
                  No hay horarios disponibles para esta fecha.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {slots.map((slot) => {
                    const isSelected = formState.scheduledTime === slot.time
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={!slot.available}
                        onClick={() => handleSlotSelect(slot)}
                        className={cn(
                          'px-3 py-2 rounded-md text-sm font-medium border transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                          isSelected
                            ? 'bg-[var(--primary)] text-[var(--primary-foreground)] border-[var(--primary)]'
                            : slot.available
                              ? 'bg-[var(--background)] border-[var(--border)] hover:border-[var(--primary)] cursor-pointer'
                              : 'bg-[var(--secondary)] text-[var(--foreground-muted)] border-[var(--border)] cursor-not-allowed opacity-50',
                        )}
                      >
                        {slot.time}
                        {slot.available && slot.remainingCapacity > 0 && (
                          <span className="ml-1 text-xs opacity-70">({slot.remainingCapacity})</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Selected slot badge */}
              {formState.scheduledTime && (
                <div className="mt-3">
                  <Badge variant="success">
                    <Check className="h-3 w-3 mr-1" />
                    Horario seleccionado: {formState.scheduledTime} - {formState.endTime}
                  </Badge>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Step 4: Paciente                                                  */}
      {/* ================================================================= */}
      <Card className={cn('transition-opacity', !step4Enabled && 'opacity-50 pointer-events-none')}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            4. Paciente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--foreground-muted)]" />
            <Input
              placeholder="Buscar por nombre o documento..."
              value={patientSearch}
              onChange={(e) => {
                setPatientSearch(e.target.value)
                // Clear selection when search changes
                if (formState.patientId) {
                  setFormState((prev) => ({
                    ...prev,
                    patientId: '',
                    patientLabel: '',
                    patientDocument: '',
                  }))
                }
              }}
              className="pl-9"
            />
          </div>

          {/* Search results */}
          {patientSearch.length >= 2 && (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {patientsLoading ? (
                <div className="flex items-center gap-2 py-4 justify-center text-sm text-[var(--foreground-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando pacientes...
                </div>
              ) : !patientsData?.items || patientsData.items.length === 0 ? (
                <p className="py-4 text-center text-sm text-[var(--foreground-muted)]">
                  No se encontraron pacientes
                </p>
              ) : (
                patientsData.items.map((patient) => {
                  const isSelected = formState.patientId === patient.id
                  const fullName = [patient.firstName, patient.secondName, patient.lastName, patient.secondLastName]
                    .filter(Boolean)
                    .join(' ')

                  return (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => handlePatientSelect(patient)}
                      className={cn(
                        'w-full text-left p-3 rounded-lg border transition-colors',
                        isSelected
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                          : 'border-[var(--border)] hover:border-[var(--primary)]',
                      )}
                    >
                      <div className="font-medium text-sm text-[var(--foreground)]">
                        {fullName}
                      </div>
                      <div className="text-xs text-[var(--foreground-muted)]">
                        {patient.document.type} {patient.document.number}
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          )}

          {/* Selected patient badge */}
          {formState.patientId && (
            <Badge variant="success">
              <Check className="h-3 w-3 mr-1" />
              {formState.patientLabel} &mdash; {formState.patientDocument}
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Step 5: Confirmar                                                 */}
      {/* ================================================================= */}
      <Card className={cn('transition-opacity', !step5Enabled && 'opacity-50 pointer-events-none')}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Check className="h-5 w-5" />
            5. Confirmar Cita
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary grid */}
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <span className="font-medium text-[var(--foreground-muted)]">Modalidades</span>
            <span className="text-[var(--foreground)]">{formState.modalityLabels.join(', ')}</span>

            <span className="font-medium text-[var(--foreground-muted)]">Profesional</span>
            <span className="text-[var(--foreground)]">{formState.providerLabel}</span>

            <span className="font-medium text-[var(--foreground-muted)]">Fecha</span>
            <span className="text-[var(--foreground)]">
              {formState.selectedDate ? formatDateSpanish(formState.selectedDate) : ''}
            </span>

            <span className="font-medium text-[var(--foreground-muted)]">Horario</span>
            <span className="text-[var(--foreground)]">
              {formState.scheduledTime && formState.endTime
                ? `${formState.scheduledTime} - ${formState.endTime}`
                : ''}
            </span>

            <span className="font-medium text-[var(--foreground-muted)]">Paciente</span>
            <span className="text-[var(--foreground)]">
              {formState.patientLabel}
              {formState.patientDocument && (
                <span className="text-[var(--foreground-muted)]"> &mdash; {formState.patientDocument}</span>
              )}
            </span>
          </div>

          {/* Notes */}
          <div>
            <label
              htmlFor="appointment-notes"
              className="block text-sm font-medium text-[var(--foreground)] mb-1.5"
            >
              Notas (opcional)
            </label>
            <textarea
              id="appointment-notes"
              rows={3}
              placeholder="Indicaciones especiales, preparaci\u00f3n requerida..."
              value={formState.notes}
              onChange={(e) => setFormState((prev) => ({ ...prev, notes: e.target.value }))}
              className={cn(
                'flex w-full rounded-md border border-[var(--border)] bg-[var(--input)] px-3 py-2 text-sm',
                'text-[var(--input-foreground)] placeholder:text-[var(--foreground-muted)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ring-offset)]',
                'resize-none',
              )}
            />
          </div>

          {/* Submit */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(APP_ROUTES.APPOINTMENTS)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={createAppointmentMutation.isPending}
            >
              <Calendar className="h-4 w-4" />
              {formState.modalityIds.length > 1
                ? `Agendar ${formState.modalityIds.length} Citas`
                : 'Agendar Cita'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
